import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { Main } from "./pages/Main";
import { Lesson } from "./pages/Lesson";
import { Routes, Route } from "react-router-dom";
import { Registration } from "./pages/Registration";
import { Login } from "./pages/Login";
import { Profile } from "./pages/Profile";
import { Rating } from "./pages/Rating";
import { getMe, getSilver, reportPwaInstalled, sendPwaHeartbeat, trackTelegramId } from "./api/user";
import { isPwaInstalled, isStandaloneMode } from "./utils/pwaInstallPrompt";
import { computePlatformHint } from "./utils/platformHint";
import { APP_VERSION } from "./appVersion";
import { ISection, IStory, IUser } from "./type";
import { ProtectedAuthRoute } from "./components/ProtectedRoutes/Auth";
import { getCurrentLesson } from "./api/lesson";
import { load3dAnimation } from "./components/RewardLesson";
import { CloseSection } from "./components/CloseSection";
import { getCurrentStorys } from "./api/story";
import { Story } from "./pages/Story";
import { ProfileEdit } from "./pages/ProfileEdit/index";
import { UnpaymentPage } from "./components/ComplitedSection";
import { DeviceFrame } from "./components/DeviceFrame";
import { PwaUpdateBanner } from "./components/PwaUpdateBanner";
import { subscribeForPush } from "./utils/pushSubscription";
import { logClientDiagnostic } from "./utils/clientDiagnosticLog";
import { prefetchCurrentAndNextLessons } from "./utils/offlineLessonPrefetch";
import "./App.css";

let firstLoad = true;
// React.StrictMode в dev может монтировать компонент дважды.
// Делам инициализацию глобально-одноразовой, чтобы второй mount
// не запускал повторный relogin/getMe и не создавал циклы.
let initStartedGlobal = false;
// WEB-ветка: чтобы после первого 401 не спамить /api/auth/me
// при HMR/перемонтированиях.
let webGetMeInFlightGlobal = false;
let webGetMeFailedGlobal = false;

// Presence socket (Socket.IO, in-memory online/offline status).
// Module-level to survive StrictMode remounts without duplicating connections.
let presenceSocket: Socket | null = null;
let presenceSocketUserId: number | null = null;

const PWA_HEARTBEAT_MIN_MS = 4 * 60 * 60 * 1000;

function isAuthScreenPath(pathname: string): boolean {
  return pathname === "/login" || pathname === "/registration";
}

function readPushPermissionState(): "not_prompted" | "granted" | "denied" {
  try {
    if (typeof Notification === "undefined") return "not_prompted";
    if (Notification.permission === "default") return "not_prompted";
    if (Notification.permission === "granted") return "granted";
    return "denied";
  } catch {
    return "not_prompted";
  }
}

export const App = () => {
  const useOuterIframe = (() => {
    try {
      return window.innerWidth > 520 && window.self === window.top;
    } catch {
      return false;
    }
  })();

  const [user, setUser] = useState<IUser | false | "not_set">("not_set");
  const [sections, setSections] = useState<ISection[]>([]);
  const [stories, setStories] = useState<IStory[]>([]);
  const [coinSilver, setCoinSilver] = useState<number>(0);
  const lastLoadedUserIdRef = useRef<number | null>(null);
  const lastLoadedSilverUserIdRef = useRef<number | null>(null);
  const userForPwaRef = useRef<IUser | false | "not_set">("not_set");
  const initStartedRef = useRef<boolean>(false);
  const reloginInFlightRef = useRef<boolean>(false);
  const pendingTgIdRef = useRef<number | null>(null);
  const tgTrackInFlightRef = useRef<boolean>(false);
  const pwaResubscribeForUserIdRef = useRef<number | null>(null);
  const lessonPrefetchKeyRef = useRef<string>("");
  const sectionsRetryTimerRef = useRef<number | null>(null);
  const sectionsRetryAttemptRef = useRef(0);

  const clearSectionsRetryTimer = () => {
    if (sectionsRetryTimerRef.current != null) {
      window.clearTimeout(sectionsRetryTimerRef.current);
      sectionsRetryTimerRef.current = null;
    }
  };

  // Чтобы `ProtectedAuthRoute` мог вовремя подавлять редирект на `/login`,
  // фиксируем timestamp максимально рано (во время render), а не в useEffect.
  try {
    if (!sessionStorage.getItem("ww_init_ts")) {
      sessionStorage.setItem("ww_init_ts", String(Date.now()));
    }
  } catch {
    // ignore
  }

  const guestUser: IUser = {
    id: 0,
    name: "",
    phone: "",
    email: "",
    avatarLink: "no-avatar",
    paid_status: false,
    User_AccountsRelation: [],
    city: "",
    about: "",
    birthdate: undefined,
  };

  const relogin = () => {
    if (reloginInFlightRef.current) return;
    reloginInFlightRef.current = true;

    // Если пользователь уже находится на странице входа/регистрации,
    // не делаем дополнительные попытки авторизации (это вызывает циклы).
    try {
      const p = window.location.pathname;
      if (isAuthScreenPath(p)) {
        reloginInFlightRef.current = false;
        return;
      }
    } catch {
      // ignore
    }

    if (webGetMeFailedGlobal) {
      setUser(false);
      reloginInFlightRef.current = false;
      return;
    }

    webGetMeInFlightGlobal = true;
    getMe()
      .then((r) => {
        if (r.type === "success") {
          webGetMeFailedGlobal = false;
          setUser(r.user);
        } else {
          webGetMeFailedGlobal = true;
          if (r.reason === "timeout") {
            logClientDiagnostic({
              action: "auth.init_redirect_login",
              description: "getMe timeout — redirect to /login",
              endpoint: "/api/auth/me",
              method: "GET",
              additionalData: { reason: r.reason },
            });
            try {
              const pNow = window.location.pathname;
              if (!isAuthScreenPath(pNow)) {
                window.location.assign("/login?session=init_timeout");
                return;
              }
            } catch {
              // ignore
            }
          }
          // Если мы уже на странице логина/регистрации,
          // не переводим state в `false` (иначе ProtectedAuthRoute
          // продолжает редиректить в /login).
          try {
            const pNow = window.location.pathname;
            if (isAuthScreenPath(pNow)) {
              setUser("not_set");
              return;
            }
          } catch {
            // ignore
          }

          setUser(false);
        }
      })
      .finally(() => {
        webGetMeInFlightGlobal = false;
        reloginInFlightRef.current = false;
      });
  };
  useEffect(() => {
    // if (user != "not_set") return;

    // Инициализация только один раз (защита и от StrictMode remount).
    if (initStartedRef.current || initStartedGlobal) return;
    initStartedRef.current = true;
    initStartedGlobal = true;

    // If the top-level frame is rendering the mobile UI via iframe,
    // don't run auth init here (to avoid double getMe()/login flows).
    if (useOuterIframe) return;

    const params = new URLSearchParams(window.location.search);
    const urlTgId = params.get("id");
    if (urlTgId && /^[0-9]+$/.test(urlTgId)) {
      pendingTgIdRef.current = Number(urlTgId);
    }

    // `?id` больше не используется для авторизации.
    // Удаляем параметр из URL, чтобы он не влиял на повторные монтирования/переходы.
    try {
      params.delete("id");
      const qs = params.toString();
      const nextUrl =
        window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, document.title, nextUrl);
    } catch {
      // ignore
    }

    // WEB-ветка
    if (webGetMeInFlightGlobal) {
      reloginInFlightRef.current = false;
      return;
    }
    if (webGetMeFailedGlobal) {
      reloginInFlightRef.current = false;
      try {
        const pNow = window.location.pathname;
        if (!isAuthScreenPath(pNow)) setUser(false);
      } catch {
        setUser(false);
      }
      return;
    }
    relogin();
  }, [useOuterIframe]);

  const reload_lessons = () => {
    void (async () => {
      const uid = lastLoadedUserIdRef.current;
      const result = await getCurrentLesson();
      if (result.type === "success" && result.sections.length > 0) {
        sectionsRetryAttemptRef.current = 0;
        clearSectionsRetryTimer();
        setSections(result.sections);
        return;
      }
      const reason =
        result.type === "error" ? result.reason : "empty_sections_response";
      logClientDiagnostic({
        action: "lessons.reload_pending",
        description: `get_current_lessons will retry: ${reason}`,
        endpoint: "/miniapi/get_current_lessons",
        method: "POST",
        userId: uid,
        additionalData: {
          attempt: sectionsRetryAttemptRef.current + 1,
          resultType: result.type,
        },
      });
      sectionsRetryAttemptRef.current += 1;
      const delay = Math.min(
        15000,
        2000 * Math.pow(2, Math.min(sectionsRetryAttemptRef.current - 1, 4))
      );
      clearSectionsRetryTimer();
      sectionsRetryTimerRef.current = window.setTimeout(() => {
        sectionsRetryTimerRef.current = null;
        if (uid != null && lastLoadedUserIdRef.current !== uid) return;
        reload_lessons();
      }, delay);
    })();
  };
  const reload_storys = () => {
    getCurrentStorys()
      .then((r) => {
        setStories(r.stories);
      })
      .catch(() => {
        // Не блокируем UI из-за временной ошибки загрузки историй.
      });
  };
  useEffect(() => {
    if (user === "not_set" || user === false) {
      clearSectionsRetryTimer();
      sectionsRetryAttemptRef.current = 0;
      lastLoadedUserIdRef.current = null;
      lastLoadedSilverUserIdRef.current = null;
      return;
    }

    // При успешной WEB-авторизации разрешаем повторные проверки.
    webGetMeFailedGlobal = false;
    webGetMeInFlightGlobal = false;

    // Если релогин дергается повторно, но пользователь тот же,
    // не пере-загружаем уроки/сторис (иначе получаем "циклические" запросы).
    const currentUserId = (user as IUser).id;
    if (lastLoadedUserIdRef.current !== currentUserId) {
      lastLoadedUserIdRef.current = currentUserId;
      sectionsRetryAttemptRef.current = 0;
      clearSectionsRetryTimer();
      reload_lessons();
      reload_storys();
    }
    if (lastLoadedSilverUserIdRef.current !== currentUserId) {
      lastLoadedSilverUserIdRef.current = currentUserId;
      getSilver()
        .then((r1) => setCoinSilver(r1.summ))
        .catch(() => {
          setCoinSilver(0);
        });
    }
    if (firstLoad) {
      setTimeout(() => load3dAnimation(), 1500);
      firstLoad = false;
    }
  }, [user]);

  // If app is opened from Telegram with `?id=<tg_id>`, remember it in DB.
  // Authentication always happens via JWT cookies.
  useEffect(() => {
    if (user === "not_set" || user === false) return;
    if (pendingTgIdRef.current == null) return;
    if (tgTrackInFlightRef.current) return;

    tgTrackInFlightRef.current = true;
    const tgIdToTrack = pendingTgIdRef.current;
    pendingTgIdRef.current = null;

    trackTelegramId(tgIdToTrack)
      .catch(() => {
        // ignore tracking errors; auth must remain unaffected.
      })
      .finally(() => {
        tgTrackInFlightRef.current = false;
      });
  }, [user]);

  useEffect(() => {
    userForPwaRef.current = user;
  }, [user]);

  useEffect(() => {
    return () => clearSectionsRetryTimer();
  }, []);

  // Мобильная клавиатура: поднимаем нижние CTA-кнопки (Далее/Войти/Сохранить пароль),
  // не сдвигая всю форму вверх. Используется в CSS через var(--kb-offset).
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;

    const vv = window.visualViewport;
    const applyViewportOffset = () => {
      const offset = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
      document.documentElement.style.setProperty("--kb-offset", `${Math.round(offset)}px`);
    };

    applyViewportOffset();
    vv.addEventListener("resize", applyViewportOffset);
    vv.addEventListener("scroll", applyViewportOffset);
    return () => {
      vv.removeEventListener("resize", applyViewportOffset);
      vv.removeEventListener("scroll", applyViewportOffset);
      document.documentElement.style.setProperty("--kb-offset", "0px");
    };
  }, []);

  useEffect(() => {
    if (user === false) {
      pwaResubscribeForUserIdRef.current = null;
      lessonPrefetchKeyRef.current = "";
    }
  }, [user]);

  // После логина прогреваем офлайн-кэш: текущее задание + 3 следующих.
  // Догрузка выполняется лениво в фоне (SW + fallback fetch).
  useEffect(() => {
    if (user === "not_set" || user === false) return;
    if (!Array.isArray(sections) || sections.length === 0) return;

    const u = user as IUser;
    const key = `${u.id}:${sections.map((s) => `${s.id}-${s.UserLessonsScheduled?.length || 0}-${s.Lesson_SectionRelation?.length || 0}`).join("|")}`;
    if (lessonPrefetchKeyRef.current === key) return;
    lessonPrefetchKeyRef.current = key;

    prefetchCurrentAndNextLessons(sections)
      .then((urls) => {
        logClientDiagnostic({
          action: "offline.lessons_prefetch_scheduled",
          description: "Prefetch current lesson and next 3",
          endpoint: "/miniapi/get_current_lessons",
          method: "POST",
          userId: u.id,
          additionalData: { urlsCount: urls.length },
        });
      })
      .catch((e) => {
        const msg = e && typeof e === "object" && "message" in e ? String((e as Error).message) : "prefetch_failed";
        logClientDiagnostic({
          action: "offline.lessons_prefetch_error",
          description: msg,
          endpoint: "/miniapi/get_current_lessons",
          method: "POST",
          userId: u.id,
        });
      });
  }, [user, sections]);

  // Установленная PWA → флаг в БД для админки (standalone или только что поставили с главного экрана).
  useEffect(() => {
    if (user === "not_set" || user === false) return;
    if (!isStandaloneMode() && !isPwaInstalled()) return;
    reportPwaInstalled().catch(() => {
      // не ломаем сессию
    });
  }, [user]);

  useEffect(() => {
    const onAppInstalled = () => {
      const u = userForPwaRef.current;
      if (u === "not_set" || u === false) return;
      reportPwaInstalled().catch(() => {});
      // После установки PWA Chrome/Windows часто пересвязывает уведомления с приложением.
      // Делаем force-resubscribe, чтобы push-подписка соответствовала именно PWA-контексту.
      subscribeForPush("pwa", { forceResubscribe: true }).catch(() => {});
    };
    window.addEventListener("appinstalled", onAppInstalled);
    return () => window.removeEventListener("appinstalled", onAppInstalled);
  }, []);

  // Только в standalone / после установки PWA: пересоздаём подписку под контекст приложения.
  // В обычной вкладке браузера этого делать нельзя — иначе в БД помечается pwa, гасятся
  // другие endpoint'ы (раньше на бэке), и установленная PWA перестаёт получать пуши.
  useEffect(() => {
    if (user === "not_set" || user === false) return;
    if (!isStandaloneMode() && !isPwaInstalled()) return;
    const u = user as IUser;
    if (pwaResubscribeForUserIdRef.current === u.id) return;
    pwaResubscribeForUserIdRef.current = u.id;
    try {
      if (typeof Notification !== "undefined" && Notification.permission !== "granted") return;
    } catch {
      // ignore and try anyway
    }
    subscribeForPush("pwa", { forceResubscribe: true }).catch(() => {});
  }, [user]);

  // PWA heartbeat (throttling): платформа, версия, push permission для админ-статистики.
  useEffect(() => {
    if (user === "not_set" || user === false) return;
    if (!isStandaloneMode()) return;

    const tick = () => {
      let last = 0;
      try {
        last = Number(sessionStorage.getItem("ww_pwa_hb_ts") || "0");
      } catch {
        /* ignore */
      }
      if (Date.now() - last < PWA_HEARTBEAT_MIN_MS) return;
      try {
        sessionStorage.setItem("ww_pwa_hb_ts", String(Date.now()));
      } catch {
        /* ignore */
      }
      sendPwaHeartbeat({
        platformHint: computePlatformHint(),
        appVersion: APP_VERSION,
        pushPermissionState: readPushPermissionState(),
      }).catch(() => {});
    };

    tick();
    const id = window.setInterval(tick, PWA_HEARTBEAT_MIN_MS);
    return () => window.clearInterval(id);
  }, [user]);

  // Presence: notify backend when user enters/leaves the app.
  useEffect(() => {
    const currentUserId = user !== "not_set" && user !== false ? (user as IUser).id : null;

    if (!currentUserId) {
      if (presenceSocket && presenceSocketUserId) {
        presenceSocket.emit("student:offline", { userId: presenceSocketUserId });
      }
      presenceSocketUserId = null;
      return;
    }

    // Create socket lazily.
    if (!presenceSocket) {
      presenceSocket = io(window.location.origin, {
        path: "/socket.io",
        withCredentials: true,
      });

      presenceSocket.on("connect", () => {
        presenceSocket?.emit("presence:join", { role: "student" });
      });

      presenceSocket.on("auth:force-logout", (payload) => {
        const uid = payload?.userId;
        const reason = payload?.reason;
        if (presenceSocketUserId && uid && Number(uid) === Number(presenceSocketUserId)) {
          setUser(false);
          try {
            const p = window.location.pathname;
            if (p !== "/login") window.location.pathname = "/login";
          } catch {
            // ignore
          }
        }
        try {
          if (localStorage.getItem("ww_presence_debug") === "1") {
            // eslint-disable-next-line no-console
            console.log("[presence] auth:force-logout", { uid, reason });
          }
        } catch {
          // ignore
        }
      });

      // Optional debug: enable with localStorage.ww_presence_debug="1".
      try {
        if (localStorage.getItem("ww_presence_debug") === "1") {
          presenceSocket.on("student:presence", (payload) => {
            // eslint-disable-next-line no-console
            console.log("[presence] student:presence", payload);
          });
        }
      } catch {
        // ignore
      }
    }

    const emitOnline = () => {
      if (!presenceSocket) return;
      presenceSocketUserId = currentUserId;
      presenceSocket.emit("student:online", { userId: currentUserId });
    };

    if (presenceSocket.connected) emitOnline();
    else presenceSocket.once("connect", emitOnline);

    const onBeforeUnload = () => {
      if (!presenceSocket || !currentUserId) return;
      presenceSocket.emit("student:offline", { userId: currentUserId });
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (presenceSocketUserId === currentUserId && presenceSocket) {
        presenceSocket.emit("student:offline", { userId: currentUserId });
      }
      if (presenceSocketUserId === currentUserId) presenceSocketUserId = null;
    };
  }, [user]);

  return (
    <>
      <PwaUpdateBanner />
      <DeviceFrame>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedAuthRoute user={user} sections={sections}>
                <Main
                  user={user as IUser}
                  sections={sections}
                  stories={stories}
                  reload_lessons={reload_lessons}
                  coinSilver={coinSilver}
                />
              </ProtectedAuthRoute>
            }
          />
          <Route
            path="/section/:secId"
            element={<Lesson sections={sections} reload_lessons={reload_lessons} />}
          />
          <Route
            path="/section/:secId/lesson/:lessonid" //HabitDayPadItem
            element={<Lesson sections={sections} reload_lessons={reload_lessons} />}
          />
          <Route path="/unpayment" element={<UnpaymentPage />} />
          <Route path="/closeSection" element={<CloseSection />} />
          <Route
            path="/registration"
            element={
              <Registration
                setUser={relogin}
                onRegistrationSuccess={(userData) => {
                  setUser(userData);
                }}
              />
            }
          />
          <Route
            path="/login"
            element={
              <Login
                onLoginSuccess={(userData) => {
                  setUser(userData);
                }}
              />
            }
          />
          <Route
            path="/profile"
            element={<Profile user={(user !== false && user !== "not_set" ? user : guestUser) as IUser} setUser={relogin} coin={coinSilver} />}
          />
          <Route
            path="/profile/edit"
            element={
              <ProtectedAuthRoute user={user} sections={sections}>
                <ProfileEdit user={user as IUser} relogin={relogin} />
              </ProtectedAuthRoute>
            }
          />
          <Route path="/rating" element={<Rating />} />
          <Route
            path="/story/:storyId"
            element={
              <Story
                stories={stories}
                reload_stories={reload_storys}
                user_id={user !== "not_set" && user !== false ? (user as IUser).id : 0}
              />
            }
          />
        </Routes>
      </DeviceFrame>
    </>
  );
};

export default App;
